import {STORE_TYPES, STRATEGY_TYPES} from '../utils/common';
import tracker from 'tracking';
import store from 'store';
import {withoutVat} from 'utils/number/applyVat';
import {
  bidManagerService,
  bidManagerAutostrategiesService,
  placementsService,
  webanalyticsService,
  labelsService
} from 'services';
import {getBiddableTypes, getStrategyType} from 'store/modules/bidmanager/utils/utils';
import ApiBadRequestError from 'utils/api/errors/ApiBadRequestError';
import {compileObjectFromKeyArray, compileVariable} from 'utils/object/objectTemplateCompiler';
import ProfileMatcher from 'legacy/modules/webanalytics/ProfileMatcher';
import GoalMatcher from 'legacy/modules/webanalytics/GoalMatcher';
import {getDefaultState, DEFAULT_RUN_TYPE} from '..';


function parseField (field) {

  // set dependencies field from composedField
  const profileMatch = field ? field.match(/profile_(\d+)_(.+)/) : null;
  const goalMatch = field ? field.match(/goal_(\d+)_(.+)/) : null;
  if (profileMatch) {

    // profile field set
    return {
      profileId: Number(profileMatch[1]),
      goalId: null,
      simpleField: profileMatch[2]
    };
  }
  if (goalMatch) {

    // goal field set
    return {
      profileId: null,
      goalId: Number(goalMatch[1]),
      simpleField: goalMatch[2]
    };
  }
  return {

    // common field set
    profileId: null,
    goalId: null,
    simpleField: field
  };
}


export default {

  // 1 BidManagerGroupListPage
  async [STORE_TYPES.SET_CURRENT_BIDMANAGER_GROUP_ID] (context, groupId) {
    if (!groupId) {
      context.commit(STORE_TYPES.SET_CURRENT_BIDMANAGER_GROUP, null);
      return;
    }
    const group = await bidManagerService.get(
      context.rootGetters['accounts/currentAccountId'],
      context.rootGetters['projects/currentProjectId'],
      groupId
    );
    context.commit(STORE_TYPES.SET_CURRENT_BIDMANAGER_GROUP, group);
  },
  async [STORE_TYPES.FETCH_PLACEMENTS] (context) {
    const data = await placementsService.getProjectPlacements(
      context.rootGetters['accounts/currentAccountId'],
      context.rootGetters['projects/currentProjectId']
    );

    context.commit(STORE_TYPES.SET_PLACEMENTS, data);
  },
  async [STORE_TYPES.FETCH_GROUPS] (context, params = {}) {

    const res = await bidManagerService.getGroups(
      context.rootGetters['accounts/currentAccountId'],
      context.rootGetters['projects/currentProjectId'],
      {
        page: context.state.currentPage,
        per_page: context.state.perPage,
        sorting: context.getters.sortingParams,
        ...params,
        perPage: null
      }
    );

    context.commit(STORE_TYPES.SET_GROUPS_TOTAL, res.items_total);

    const data = res.items;
    data.forEach(group => {
      group.progressMetricsStats = context.getters.progressDataMap[group.id] || null;
      group.items = {
        labels: group.labels,
        placementIds: group.placement_ids
      };
    });

    context.commit(STORE_TYPES.SET_GROUPS, data);

    if (context.rootGetters['user/canUser']('READ_COST_FACT')) {
      const groupIds = data.map(group => group.id);
      await context.dispatch(STORE_TYPES.FETCH_PROGRESS_METRICS_COLUMN, {groupIds});
    }
  },
  async [STORE_TYPES.FETCH_PROGRESS_METRICS_COLUMN] (context, {groupIds, options = {}}) {
    if (!options.suppressProgressLoader) {
      context.commit(STORE_TYPES.SET_PROGRESS_METRICS_LOADING, true);
    }
    try {
      const data = (
        await bidManagerService.getGroupProgress(
          context.rootGetters['accounts/currentAccountId'],
          context.rootGetters['projects/currentProjectId'],
          groupIds
        )
      ).items;
      context.commit(STORE_TYPES.SET_PROGRESS_METRICS_LOADING, false);

      if (options.updateSingleGroup) {
        const pd = context.state.progressData;
        const fetchedGroupIdx = pd.findIndex(p => p.id === data[0].id);

        const newProgressData = [
          ...pd.slice(0, fetchedGroupIdx),
          data[0],
          ...pd.slice(fetchedGroupIdx + 1)
        ];

        context.commit(STORE_TYPES.SET_PROGRESS_DATA, newProgressData);
      } else {
        context.commit(STORE_TYPES.SET_PROGRESS_DATA, data);
      }

    } catch (e) {
      context.commit(STORE_TYPES.SET_PROGRESS_METRICS_LOADING, false);
      context.commit(STORE_TYPES.SET_PROGRESS_DATA, []);
    }
  },
  async [STORE_TYPES.DELETE_GROUP_REQUEST] (context, {accountId, projectId, id}) {
    await bidManagerService.delete(accountId, projectId, id);
    await context.dispatch(STORE_TYPES.FETCH_GROUPS);
  },
  async [STORE_TYPES.TOGGLE_ENABLING] (context, {accountId, projectId, id, enable}) {
    if (enable) {
      return await bidManagerService.activateEnabling(accountId, projectId, id);
    }
    return await bidManagerService.deactivateEnabling(accountId, projectId, id);
  },
  async [STORE_TYPES.FETCH_PROJECT_ANALYTICS] (context, {accountId, projectId}) {
    await context.dispatch('bidmanagerAnalytics/FETCH_PROJECT_ANALYTICS', {accountId, projectId});

    const profiles = context.getters['bidmanagerAnalytics/projectProfiles'];
    const goals = context.getters['bidmanagerAnalytics/projectGoals'];


    context.commit(STORE_TYPES.SET_FETCHED_PROFILES, profiles);
    context.commit(STORE_TYPES.SET_FETCHED_GOALS, goals);
  },

  // 2 Bidmanager
  async [STORE_TYPES.COPY_FROM_GROUP] (context, {toProjectId, fromGroup}) {
    const copyFromGroup = await bidManagerService.get(fromGroup.accountId, fromGroup.projectId, fromGroup.id);
    const initData = {
      ...copyFromGroup,
      id: null,
      placement_ids: [],
      labels: [],
      manageOn: ''
    };
    const channelId = initData.channel_id;
    await context.dispatch(STORE_TYPES.FETCH_PARAMS, {projectId: toProjectId, channelId});

    const parsedConditions = initData.options.rules.flatMap(rule => rule.conditions).map(condition => {
      const {simpleField, profileId, goalId} = parseField(condition.field);
      return {
        condition,
        goalId,
        profileId,
        simpleField
      };
    });

    // remove all unavailable fields for current project
    parsedConditions.forEach(parsedCondition => {
      if (!context.getters.fieldsMapping[parsedCondition.simpleField]) {
        parsedCondition.condition.field = null;
        parsedCondition.goalId = null;
        parsedCondition.profileId = null;
      }
    });


    // match goals and profiles by xid if needed
    if (fromGroup.projectId !== toProjectId) {

      // determine whether group has analytics conditions
      let hasGoals = false;
      let hasProfiles = false;
      for (const condition of parsedConditions) {
        hasGoals = hasGoals || Boolean(condition.goalId);
        hasProfiles = hasProfiles || Boolean(condition.profileId);
      }

      if (hasProfiles || hasGoals) {
        const fromProfiles = await webanalyticsService.getProfilesList(fromGroup.accountId, fromGroup.projectId);

        const profileMatcher = new ProfileMatcher(fromProfiles, context.state.profiles);
        if (hasProfiles) {
          parsedConditions.forEach(parsedCondition => {
            const toProfile = profileMatcher.match(parsedCondition.profileId);
            if (toProfile) {
              parsedCondition.condition.field =
                `profile_${toProfile.id}_${parsedCondition.simpleField}`;
            }
          });
        }
        if (hasGoals) {
          const fromGoals = await webanalyticsService.getGoalsList(fromGroup.accountId, fromGroup.projectId);
          const goalMatcher = new GoalMatcher(profileMatcher, fromGoals, context.state.goals);
          parsedConditions.forEach(parsedCondition => {
            if (parsedCondition.goalId) {
              const toGoal = goalMatcher.match(parsedCondition.goalId);
              if (toGoal) {
                parsedCondition.condition.field =
                  `goal_${toGoal.id}_${parsedCondition.simpleField}`;
              }
            }
          });
        }
      }
    }
    context.dispatch(STORE_TYPES.INIT_GROUP, initData);
    context.commit(STORE_TYPES.SET_ENABLE_ON, null);

  },
  [STORE_TYPES.INIT_GROUP] (context, initData) {
    const strategyType = getStrategyType(initData);
    context.dispatch(STORE_TYPES.SET_STRATEGY_TYPE, strategyType);
    context.commit(STORE_TYPES.INIT_GROUP, initData);
  },
  async [STORE_TYPES.FETCH_GROUP] (context, [accountId, projectId, groupId]) {
    const group = await bidManagerService.get(accountId, projectId, groupId);

    // crutch for task about the launch rate "every 15 minutes"
    if (group.run_type !== DEFAULT_RUN_TYPE) {
      group.time_of_day = [group.run_type];
    }

    context.dispatch(STORE_TYPES.INIT_GROUP, group);

    const channelId = group.channel_id;

    context.commit(STORE_TYPES.IS_NEW, false);
    context.commit(STORE_TYPES.SET_ENABLE_ON, null);

    await context.dispatch(STORE_TYPES.FETCH_PARAMS, {accountId, projectId, channelId});
    context.commit(STORE_TYPES.FIX_SERVER_GROUP_STATE);
  },
  async [STORE_TYPES.FETCH_PARAMS] (context, payload) {
    const {accountId, projectId, channelId} = payload;

    let biddableTypes = [];

    const [params, labels, placements, progressMetrics] = await Promise.all([
      bidManagerService.getParams(accountId, projectId, channelId),
      labelsService.getProjectLabels(accountId, projectId, channelId),
      placementsService.getProjectPlacements(accountId, projectId, {channel_id: channelId}),
      context.getters.canUseProgressMetrics
        ? await bidManagerService.getProgressMetricsList(accountId, projectId) : [],
      context.dispatch(STORE_TYPES.FETCH_PROJECT_ANALYTICS, {accountId, projectId})
    ]);

    // wrap type with array if it's not field with multiple inputs.
    for (let group of params.fields) {
      for (let field of group.fields) {
        if (!_.isArray(field.types)) {
          field.types = [field.types];
        }
      }
    }

    if (Array.isArray(params.biddables) && params.biddables.length) {
      biddableTypes = params.biddables;
      context.commit(STORE_TYPES.IS_SELECTABLE_BIDDABLE_FOR_PLACEMENT, true);
    } else {
      biddableTypes = getBiddableTypes(channelId);
    }

    if (context.getters.isNew || !context.getters.biddable || context.getters.isSelectableBiddableForPlacement) {
      if (context.getters.group.biddable) {
        context.commit(STORE_TYPES.UPDATE_GROUP_OPTIONS, {biddable: context.getters.group.biddable.toUpperCase()});
      } else if (biddableTypes?.length) {

        // set default channel biddable if channel has biddable choices
        context.commit(STORE_TYPES.UPDATE_GROUP_OPTIONS, {biddable: biddableTypes[0].id});
      }
    }
    context.commit(STORE_TYPES.SET_FETCHED_LABELS, labels);
    context.commit(STORE_TYPES.SET_FETCHED_PLACEMENTS, placements);
    context.commit(STORE_TYPES.SET_FETCHED_PROGRESS_METRICS, progressMetrics);

    context.commit(STORE_TYPES.SET_BIDDABLE_TYPES, biddableTypes);

    context.commit(STORE_TYPES.IS_LOGGING_AVAILABLE, true);

    context.commit(STORE_TYPES.SET_FETCHED_PARAMS, params);
  },
  async [STORE_TYPES.SAVE_GROUP] (context, meta) {
    const {accountId, projectId, force_autocampaign_save = false} = meta;

    context.commit(STORE_TYPES.SET_ERRORS, {});

    tracker.track('bidder_save');
    try {
      let group;

      const groupData = {
        ...context.getters._group_to_save,
        force_autocampaign_save
      };

      if (context.getters._group_to_save.id) {
        group = await bidManagerService.update(
          accountId, projectId, context.getters._group_to_save.id,
          groupData);
      } else {
        group = (await bidManagerService.create(
          accountId, projectId,
          groupData)).data;
      }

      tracker.track('bidder_save_success', {
        id: group.id,
        management: group.biddable,
        channel_id: context.rootGetters['channels/getChannelById'](group.channel_id).name,
        operate: group.labels.length ? 'labels' : 'placements',
        status: group.enabled,
        progress_metrics: group.progress_metrics.metrics,
        strategy_type: context.getters.strategyType
      });
      context.commit(STORE_TYPES.FIX_SERVER_GROUP_STATE);
      return Promise.resolve(group);
    } catch (error) {
      if (error instanceof ApiBadRequestError) {
        context.commit(STORE_TYPES.SET_ERRORS, error.getValidationErrors());
      }

      throw error;
    }
  },
  [STORE_TYPES.RESET_RULES] (context) {
    context.commit(STORE_TYPES.SET_ERRORS, {});
    context.commit(STORE_TYPES.SET_VALIDATION_RULES_ERRORS, {});
    context.commit(STORE_TYPES.RESET_RULES);
    context.commit(STORE_TYPES.ADD_DEFAULT_RULE);
  },
  [STORE_TYPES.RESET_GROUP_AFTER_PLACEMENT_CHANGE] (context) {
    const mapGroupKey = [
      'progress_metrics',
      'day_of_week',
      'time_of_day',
      'day_of_week',
      'day_of_week',
      'day_of_week'
    ];

    context.commit(STORE_TYPES.UPDATE_GROUP, compileObjectFromKeyArray(getDefaultState().group, mapGroupKey));
    context.commit(STORE_TYPES.SET_ENABLE_ON, getDefaultState().enableOn);
    context.commit(STORE_TYPES.SET_PLACEMENT_IDS, []);
  },
  [STORE_TYPES.RESET_STRATEGY] (context) {
    context.commit(STORE_TYPES.RESET_AUTOSTRATEGY);
    context.commit(STORE_TYPES.SET_STRATEGY_TYPE, null);
    context.commit(STORE_TYPES.SET_ERRORS, {});
    context.commit(STORE_TYPES.SET_VALIDATION_RULES_ERRORS, {});
  },
  async [STORE_TYPES.CHANGE_CHANNEL] (context, payload) {
    context.commit(STORE_TYPES.SET_VALIDATION_RULES_ERRORS, {});
    context.commit(STORE_TYPES.SET_ERRORS, {});
    context.commit(STORE_TYPES.SET_LOADING_CHANNEL_STATE, true);
    context.commit(STORE_TYPES.CHANGE_CHANNEL, payload.channelId);

    await context.dispatch(STORE_TYPES.FETCH_PARAMS, payload);
    context.dispatch(STORE_TYPES.RESET_RULES);
    context.dispatch(STORE_TYPES.RESET_STRATEGY);

    // reset labels, placements and biddable
    context.commit(STORE_TYPES.SET_LABELS, []);
    context.commit(STORE_TYPES.SET_PLACEMENT_IDS, []);
    context.commit(STORE_TYPES.SET_LOADING_CHANNEL_STATE, false);
    context.commit(STORE_TYPES.FIX_SERVER_GROUP_STATE);
  },
  [STORE_TYPES.SET_BIDDABLE_TYPE] (context, type) {
    context.commit(STORE_TYPES.UPDATE_GROUP_OPTIONS, {biddable: type});

    // disable fallback url on biddable type change
    context.commit(STORE_TYPES.TOGGLE_FALLBACK_ACTION_STATE, false);
    context.dispatch(STORE_TYPES.RESET_RULES);
  },
  [STORE_TYPES.SET_STRATEGY_TYPE] (context, type) {
    context.commit(STORE_TYPES.SET_STRATEGY_TYPE, type);
    context.commit(STORE_TYPES.SET_ERRORS, {});
    context.commit(STORE_TYPES.SET_VALIDATION_RULES_ERRORS, {});
  },
  [STORE_TYPES.RESET_AUTOSTRATEGY_ARGUMENTS] (context, fields) {
    context.commit(STORE_TYPES.RESET_AUTOSTRATEGY_ARGUMENTS, fields);
  },
  [STORE_TYPES.SET_AUTOSTRATEGY_ARGUMENT] (context, payload) {
    context.commit(STORE_TYPES.SET_AUTOSTRATEGY_ARGUMENT, payload);
    const [argumentId, argumentValue] = payload;
    const argument = context.getters.autostrategyArgumentsMap[argumentId];
    if (argument.populates) {
      const argumentToPopulate = context.getters.autostrategyArgumentsMap[argument.populates];
      if (argumentToPopulate.type === 'wa_profile') {
        const profileId = context.getters.goalsMap[argumentValue].profile_id;
        context.commit(STORE_TYPES.SET_AUTOSTRATEGY_ARGUMENT, [argumentToPopulate.id, profileId]);
      }
    }
  },
  [STORE_TYPES.SET_AUTOSTRATEGY_ID] (context, payload) {
    context.commit(STORE_TYPES.SET_AUTOSTRATEGY_ID, payload);
    context.commit(STORE_TYPES.RESET_AUTOSTRATEGY_ARGUMENTS,
      context.getters.autostrategyArguments.map(argument => argument.id)
    );
    context.commit(STORE_TYPES.SET_ERRORS, {});
    context.commit(STORE_TYPES.SET_VALIDATION_RULES_ERRORS, {});
  },

  /*
   * Смена варианта одной и той же стратегии: в отличие от SET_AUTOSTRATEGY_ID значения аргументов сохраняются,
   * потому что для пользователя это переключение опции внутри формы, а не выбор другой стратегии.
   */
  [STORE_TYPES.SWITCH_AUTOSTRATEGY_VARIANT] (context, payload) {
    context.commit(STORE_TYPES.SET_AUTOSTRATEGY_ID, payload);
    context.commit(STORE_TYPES.SET_ERRORS, {});
    context.commit(STORE_TYPES.SET_VALIDATION_RULES_ERRORS, {});
  },
  async [STORE_TYPES.FETCH_STRATEGIES_LIST] (context, projectId) {
    let list = await bidManagerAutostrategiesService.list(projectId);
    list = list.map(autostrategy => {
      autostrategy.arguments = autostrategy.arguments.map(argument => {
        if (argument?.args && (argument.args.default === '1' || argument.args.default === 1)) {
          argument.args.default = withoutVat(argument.args.default, store.getters['accounts/vatDivider']);
        }
        return argument;
      });

      return autostrategy;
    });


    context.commit(STORE_TYPES.SET_STRATEGIES_LIST, list);
  },
  [STORE_TYPES.CONVERT_AUTOSTRATEGY_TO_MANUAL_STRETEGY] (context, {autostrategy, argumentsValues}) {
    const compiledTemplate = compileVariable(autostrategy.template, argumentsValues);
    context.commit(STORE_TYPES.UPDATE_GROUP, compiledTemplate);

    // convert only enabled rules
    compiledTemplate.options.rules = compiledTemplate.options.rules.filter(r => r.enabled);
    context.commit(STORE_TYPES.UPDATE_GROUP_OPTIONS, compiledTemplate.options);
    context.commit(STORE_TYPES.SET_STRATEGY_TYPE, STRATEGY_TYPES.MANUAL);
  }
};
